import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";

// Admin → Artist Event Types: every event type artists can choose ("Where do
// you perform?"), how many artists chose it and who they are. Used to decide
// which event types to feature in the homepage stories slider.
export default function AdminEventTypeArtists() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [showEmpty, setShowEmpty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/homepage/event-types");
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (error) {
      toast(`Could not load event types: ${formatApiError(error)}`, "error");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const visible = showEmpty ? items : items.filter((item) => item.artist_count > 0);
  const emptyCount = items.length - items.filter((item) => item.artist_count > 0).length;

  return (
    <div className="pad-24" data-testid="admin-event-type-artists">
      <h2 className="font-serif fs-24 fw-700 mb-8">Artist Event Types</h2>
      <p className="text-muted fs-13 mb-16">
        Which event types artists perform at, from their questionnaire ("Where do you perform?") and profile.
        Only live artists appear on the website, so pick event types with live artists for the homepage stories slider.
      </p>

      <div className="card card-pad">
        <div className="mb-12" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}>
          <h3 className="fs-16 fw-700">Event types ({visible.length})</h3>
          <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "center", flexWrap: "wrap" }}>
            {emptyCount > 0 && (
              <label className="fs-12" style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={showEmpty} onChange={(event) => setShowEmpty(event.target.checked)} data-testid="event-types-show-empty" />
                Show event types no artist chose ({emptyCount})
              </label>
            )}
            <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {loading && items.length === 0 ? (
          <p className="text-muted fs-13">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-muted fs-13">No artist has chosen an event type yet. Artists choose them in their questionnaire.</p>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Event type</th>
                  <th>Live artists</th>
                  <th>All artists</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => {
                  const isOpen = open === item.name;
                  return (
                    <React.Fragment key={item.name}>
                      <tr data-testid={`event-type-row-${item.name}`}>
                        <td><strong>{item.name}</strong></td>
                        <td>
                          <span className={`pill ${item.live_count >= 3 ? "pill-green" : item.live_count > 0 ? "pill-amber" : "pill-red"}`}>
                            {item.live_count}
                          </span>
                        </td>
                        <td>{item.artist_count}</td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {item.live_count > 0 && (
                            <a
                              className="btn btn-ghost btn-xs"
                              href={`/search?${new URLSearchParams({ event_type: item.name, fresh_search: "1" }).toString()}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Preview page
                            </a>
                          )}
                          {item.artist_count > 0 && (
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs"
                              onClick={() => setOpen(isOpen ? null : item.name)}
                              aria-expanded={isOpen}
                              data-testid={`event-type-toggle-${item.name}`}
                            >
                              {isOpen ? "Hide artists" : "Show artists"}
                            </button>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={4} style={{ background: "var(--bg2, rgba(0,0,0,0.02))" }}>
                            <div style={{ display: "grid", gap: "var(--space-1)" }}>
                              {item.artists.map((artist) => (
                                <div key={artist.user_id} className="fs-13" style={{ display: "flex", gap: "var(--space-2)", alignItems: "center", flexWrap: "wrap" }}>
                                  <strong>{artist.stage_name}</strong>
                                  <span className="text-muted fs-12">{[artist.category, artist.city].filter(Boolean).join(" · ")}</span>
                                  <span className={`pill ${artist.is_live ? "pill-green" : "pill-amber"}`}>{artist.is_live ? "Live" : "Not live"}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-muted fs-11 mt-12">
          Green: 3 or more live artists, a full page. Amber: 1–2. Red: none live yet, so the page would be empty.
        </p>
      </div>
    </div>
  );
}
